import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import { CloseFullscreen, DragIndicator } from '@mui/icons-material';
import { useEditor, EditorContent, EditorContext } from '@tiptap/react';
import DragHandle from '@tiptap/extension-drag-handle-react';
import { EditorState, TextSelection } from '@tiptap/pm/state';
import { Fragment } from '@tiptap/pm/model';
import { createDocument, getHTMLFromFragment } from '@tiptap/core';
import 'prosemirror-tables/style/tables.css';
import imageService from '../services/imageService';
import { useAuth } from '../context/EnterpriseAuthContext';
import { convertMermaidBlocks } from '../utils/mermaidUtils';
import { migrateLegacyPlaceholders } from '../utils/templateFields';
import { buildPatchDiffHtml } from '../utils/documentPatchUtils';
import { buildBlockIdStampTransaction } from '../extensions/BlockId';
import { PAGINATION_LIST_NODES, PAGINATION_TRANSPARENT_NODES, contentHost } from '../extensions/Pagination';
import { buildEditorExtensions } from './editor/editorExtensions';
import EditorToolbar from './editor/EditorToolbar';
import { SelectionBubbleMenu } from './editor/EditorBubbleMenu';
import GeneratedSectionChrome from './editor/GeneratedSectionChrome';
import InlineAiMenu from './editor/InlineAiMenu';
import SlashMenu from './editor/SlashMenu';
import FindReplaceBar from './editor/FindReplaceBar';
import DocumentOutline from './editor/DocumentOutline';
import EditorStatusBar from './editor/EditorStatusBar';
import ShortcutsDialog from './editor/ShortcutsDialog';
import { ReviewBar, ReviewChangeChip } from './editor/ReviewBar';
import useReviewController from './editor/useReviewController';
import { setEditorEditable } from './editor/useEditorEditable';
import { buildMergedReviewHtml, docHasReviewMarkup, docToHtml, getReviewBaselineDoc } from './editor/reviewEngine';
import { INLINE_ATOM_CHAR, applyPlanToTransaction, getTopLevelBlocks, guttedEditReason } from './editor/editPlan';
import { buildPageSx, diffSpanSx } from './editor/editorContentStyles';
import { usePageSetup } from './editor/pageSetupContext';
import { normalizePageSetup, resolvePageGeometry } from '../utils/pageSetup';
import {
  getPaperSize,
  normalizePageMode,
  normalizePaperSize,
  readPaperSize,
  subscribePaperSize,
  writePaperSize,
  PAGE_MODE_STORAGE_KEY,
} from '../utils/paperSize';

/** Debounce for propagating content to the parent (flushed on blur, save and unmount). */
const CHANGE_DEBOUNCE_MS = 300;
const OUTLINE_STORAGE_KEY = 'quill:editor:outline-open';
// PAGE_MODE_STORAGE_KEY ('quill:editor:page-mode') comes from utils/paperSize, shared with the layout context.
/** Outline column width (px). */
const OUTLINE_WIDTH = 200;
/** A4 page width in page mode (px, matches buildPageSx). */
const PAGE_WIDTH = 794;
/** Gap between the outline and the page plus the row's side padding (px). */
const OUTLINE_CHROME = 28;
/** Pages view: gray canvas padding around the sheets (px, each side). */
export const PAGES_CANVAS_PADDING = 24;
/** Pages view: gap between two sheets (px). */
export const PAGES_GAP = 24;

/**
 * Width of a sheet: a paper size id, or a sheet spec ({width}, e.g. a landscape geometry).
 * @param {'letter'|'a4'|{width: number}} [paper] - Paper size id or sheet.
 * @returns {number} Width (px).
 */
const sheetWidth = (paper) => (paper && typeof paper === 'object' && Number(paper.width) > 0 ? Number(paper.width) : getPaperSize(paper).width);

/**
 * The sheet a Pages view draws: the paper as it lies (landscape swaps it) with the page setup's
 * margins, in the shape of a paperSize spec (getPaperSize) for buildPagesSheetSx.
 * @param {'letter'|'a4'} paperSize - Paper size id.
 * @param {object|null} pageSetup - Page setup (null = defaults: portrait, 1 in margins).
 * @returns {object} Sheet spec ({width, height, margin, contentWidth, contentHeight, orientation, margins, ...}).
 */
export const pagesSheetSpec = (paperSize, pageSetup) => {
  const paper = getPaperSize(paperSize);
  const geometry = resolvePageGeometry(pageSetup, paperSize);
  return {
    ...paper,
    width: geometry.width,
    height: geometry.height,
    margin: geometry.margin.top,
    marginIn: geometry.marginIn,
    contentWidth: geometry.contentWidth,
    contentHeight: geometry.contentHeight,
    orientation: geometry.orientation,
    margins: geometry.margins,
  };
};

/**
 * Whether the outline fits beside the page without narrowing it. In page mode the page keeps its
 * full width as panels open and close: when the editor gets too narrow (Inky docked, a smaller
 * window) the outline steps aside instead of squeezing the page. In Pages view the sheet is the
 * paper width plus the canvas padding around it.
 * @param {number} rootWidth - Editor root width (px).
 * @param {'page'|'wide'|'pages'} pageMode - Page mode.
 * @param {'letter'|'a4'|{width: number}} [paperSize] - Paper size (Pages view), or the sheet (pagesSheetSpec).
 * @returns {boolean} True when the outline can be shown.
 */
export const outlineFits = (rootWidth, pageMode, paperSize) => {
  if (pageMode === 'pages') return rootWidth >= sheetWidth(paperSize) + 2 * PAGES_CANVAS_PADDING + OUTLINE_WIDTH + OUTLINE_CHROME;
  return rootWidth >= (pageMode === 'page' ? PAGE_WIDTH + OUTLINE_WIDTH + OUTLINE_CHROME : 900);
};

/** Side padding of the editor's content row from sm up (px, each side). */
const ROW_PADDING_X = 8;

/**
 * Whether the full paper-width sheet fits in the editor (measured width, not the viewport, so a
 * docked Inky panel or an open sidebar counts). When it does not, the sheet becomes fluid
 * instead of being cut off with a sideways scrollbar at the bottom of the whole document.
 * @param {number} rootWidth - Editor root width (px).
 * @param {'letter'|'a4'|{width: number}} [paperSize] - Paper size, or the sheet (pagesSheetSpec).
 * @returns {boolean} True when the sheet can keep the exact paper width.
 */
export const pagesSheetFits = (rootWidth, paperSize) => rootWidth >= sheetWidth(paperSize) + 2 * PAGES_CANVAS_PADDING + 2 * ROW_PADDING_X;

/**
 * Viewport of the nearest scrolling ancestor of an element.
 * @param {Element} el - Element.
 * @returns {{scroller: Element, top: number}|null} Scroll container and its visible top (px).
 */
const scrollViewportOf = (el) => {
  const doc = el && el.ownerDocument;
  const win = doc && doc.defaultView;
  if (!win) return null;
  for (let node = el.parentElement; node && node !== doc.body && node !== doc.documentElement; node = node.parentElement) {
    const { overflowY } = win.getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return { scroller: node, top: node.getBoundingClientRect().top };
    }
  }
  const root = doc.scrollingElement || doc.documentElement;
  return root ? { scroller: root, top: 0 } : null;
};

/** Max wrapper nesting the reading position anchor descends into (as Pagination does). */
const SCROLL_ANCHOR_DEPTH = 8;

/**
 * Remember the first block visible in the scroll viewport and where it sits, so a view change
 * (Pages, Page width, Full width, paper size) can keep the reading position.
 *
 * Blocks are the units Pagination may end a page between: a wrapper or list it splits
 * (generated section, trust section, blockquote, bullet/ordered/task list) that is cut by the
 * viewport top is looked into, down to its children or items, since a page end added or moved
 * inside it, above the viewport, would otherwise move the lines being read.
 * @param {import('@tiptap/core').Editor|null} editor - Editor.
 * @returns {{pos: number, offset: number, scroller: Element}|null} Anchor.
 */
const captureScrollAnchor = (editor) => {
  if (!editor || editor.isDestroyed || !editor.view.dom.isConnected) return null;
  const { view } = editor;
  const viewport = scrollViewportOf(view.dom);
  if (!viewport) return null;
  // The first block that starts in the upper half of the viewport is what the eye is on; a
  // block cut by the top edge is only the fallback, since a reflow (another width) changes its
  // height and would move everything below it.
  const height = viewport.scroller.clientHeight || 0;
  let straddling = null;
  let starting = null;
  const walk = (container, containerDesc, depth, items) => {
    for (const el of Array.from(container.children)) {
      if (el.hasAttribute('data-q-pagination')) continue;
      const desc = el.pmViewDesc;
      if (!desc || !desc.node || desc.parent !== containerDesc) continue;
      const rect = el.getBoundingClientRect();
      if (rect.bottom <= viewport.top) continue;
      const anchor = { pos: desc.posBefore, offset: rect.top - viewport.top, scroller: viewport.scroller };
      if (rect.top >= viewport.top) {
        starting = anchor;
        return true;
      }
      const name = desc.node.type.name;
      const isList = PAGINATION_LIST_NODES.includes(name);
      if (!items && depth < SCROLL_ANCHOR_DEPTH && (isList || PAGINATION_TRANSPARENT_NODES.includes(name))) {
        const host = contentHost(desc);
        if (host) {
          const before = straddling;
          if (walk(host, desc, depth + 1, isList)) {
            if (!straddling) straddling = anchor;
            return true;
          }
          if (straddling !== before) continue;
        }
      }
      if (!straddling) straddling = anchor;
    }
    return false;
  };
  walk(view.dom, view.docView, 0, false);
  if (starting && (starting.offset <= height / 2 || !straddling)) return starting;
  return straddling;
};

/**
 * Scroll so the anchored block is back where it was in the viewport.
 * @param {import('@tiptap/core').Editor|null} editor - Editor.
 * @param {{pos: number, offset: number, scroller?: Element}|null} anchor - From captureScrollAnchor.
 * @returns {void}
 */
const restoreScrollAnchor = (editor, anchor) => {
  if (!anchor || !editor || editor.isDestroyed) return;
  const { view } = editor;
  if (anchor.pos > view.state.doc.content.size) return;
  let el = null;
  try {
    el = view.nodeDOM(anchor.pos);
  } catch {
    el = null;
  }
  if (!el || el.nodeType !== 1) return;
  // Keep the scroller found at capture time: mid-switch its content can be briefly shorter than
  // its box, and a fresh lookup would then pick another element.
  const doc = view.dom.ownerDocument;
  const root = doc.scrollingElement || doc.documentElement;
  const { scroller } = anchor;
  const viewport = scroller && scroller.isConnected
    ? { scroller, top: scroller === root ? 0 : scroller.getBoundingClientRect().top }
    : scrollViewportOf(view.dom);
  if (!viewport) return;
  const delta = el.getBoundingClientRect().top - viewport.top - anchor.offset;
  if (Math.abs(delta) >= 1) viewport.scroller.scrollTop += delta;
};

/** How long a view change keeps the reading position pinned while the layout settles (ms). */
const SCROLL_PIN_MS = 900;
const SCROLL_PIN_STOP_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'];

/**
 * Keep the anchored block in place while a view change settles. The new layout lands in steps
 * (the sheet CSS, Pagination switching its page ends on or off and measuring in a later frame,
 * the outline column, a menu handing focus back to its button), so the anchor is restored now
 * and then on every frame for a short while. The browser's own scroll anchoring
 * (overflow-anchor) is paused meanwhile, as it moves the scroll position on each of those steps
 * too. Any scroll input from the person (wheel, touch, key, pointer) ends the pin at once.
 * @param {import('@tiptap/core').Editor|null} editor - Editor.
 * @param {{pos: number, offset: number, scroller?: Element}|null} anchor - From captureScrollAnchor.
 * @returns {() => void} Stops the pin (safe to call more than once).
 */
const pinScrollAnchor = (editor, anchor) => {
  if (!anchor || !editor || editor.isDestroyed) return () => {};
  restoreScrollAnchor(editor, anchor);
  const doc = editor.view.dom.ownerDocument;
  const win = doc && doc.defaultView;
  if (!win || typeof win.requestAnimationFrame !== 'function') return () => {};

  const root = doc.scrollingElement || doc.documentElement;
  const paused = anchor.scroller && anchor.scroller !== root
    ? [anchor.scroller]
    : [doc.documentElement, doc.body].filter(Boolean);
  const previous = paused.map((el) => el.style.overflowAnchor);
  paused.forEach((el) => { el.style.overflowAnchor = 'none'; });

  const clock = () => (win.performance && typeof win.performance.now === 'function' ? win.performance.now() : Date.now());
  const until = clock() + SCROLL_PIN_MS;
  let frame = null;
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (frame !== null) win.cancelAnimationFrame(frame);
    SCROLL_PIN_STOP_EVENTS.forEach((type) => win.removeEventListener(type, stop, true));
    paused.forEach((el, i) => { el.style.overflowAnchor = previous[i]; });
  };
  const tick = () => {
    frame = null;
    if (stopped) return;
    restoreScrollAnchor(editor, anchor);
    if (clock() >= until) stop();
    else frame = win.requestAnimationFrame(tick);
  };
  SCROLL_PIN_STOP_EVENTS.forEach((type) => win.addEventListener(type, stop, { capture: true, passive: true }));
  frame = win.requestAnimationFrame(tick);
  return stop;
};

/**
 * Pages view (print layout) sheet styling on top of the shared content styles: a paper-sized
 * sheet (landscape when the page setup says so) with the page setup's margins (1 in by default)
 * on a neutral canvas. The Pagination extension draws the page ends, gaps, headers and footers
 * inside it as widget decorations.
 *
 * CSS variables set on the sheet (light and dark come from the --q- tokens):
 *   --q-page-width, --q-page-height, --q-page-content-height: paper geometry (px).
 *   --q-page-margin-y / --q-page-margin-x: the sheet's top/bottom padding (the page margin) and side padding (the page margin from sm up).
 *   --q-page-gap: space between sheets; --q-page-canvas: canvas color (gap fill);
 *   --q-page-bleed: how far the gap band reaches past the sheet edges (Pagination reads it);
 *   --q-page-sheet: sheet color; --q-page-shadow: sheet shadow; --q-page-number: header and footer text color.
 * @param {object} baseSx - buildPageSx() output.
 * @param {ReturnType<typeof getPaperSize>} paper - Paper size spec, or the sheet from pagesSheetSpec.
 * @param {{fits?: boolean}} [opts] - fits: the full paper width fits in the editor
 *   (pagesSheetFits); otherwise the sheet is fluid (up to the paper width) with narrower side
 *   margins, so nothing is cut off. Page heights and top/bottom margins stay exact either way.
 * @returns {object} Sheet sx.
 */
export const buildPagesSheetSx = (baseSx, paper, { fits = true } = {}) => {
  const margin = `${paper.margin}px`;
  // A sheet narrower than the paper keeps at most 48 px side margins (narrow margins stay narrower).
  const fluidMargin = `${Math.min(48, paper.margin)}px`;
  return {
    ...baseSx,
    '--q-page-width': `${paper.width}px`,
    '--q-page-height': `${paper.height}px`,
    '--q-page-content-height': `${paper.contentHeight}px`,
    // Top/bottom margins stay exact everywhere so the page content height matches the exports;
    // phones only get narrower side margins.
    '--q-page-margin-y': margin,
    '--q-page-margin-x': fits ? { xs: '20px', sm: margin } : { xs: '20px', sm: fluidMargin },
    '--q-page-gap': `${PAGES_GAP}px`,
    // How far the gap band reaches past the sheet edges to hide the sheet shadow (stays inside
    // the canvas padding).
    '--q-page-bleed': { xs: '8px', sm: '12px' },
    '--q-page-canvas': 'var(--q-canvas, #EEECE7)',
    '--q-page-sheet': 'var(--q-surface, #FFFFFF)',
    '--q-page-shadow': 'var(--q-shadow-md)',
    '--q-page-number': 'var(--q-faint, #716C65)',
    // The exact paper width when it fits, so line wraps (and therefore page ends) match the
    // exports; otherwise a fluid sheet, never wider than the editor.
    width: fits ? { xs: '100%', sm: paper.width } : '100%',
    maxWidth: fits ? { xs: '100%', sm: 'none' } : paper.width,
    flexShrink: fits ? 0 : 1,
    mx: 'auto',
    minHeight: paper.height,
    bgcolor: 'var(--q-page-sheet)',
    borderRadius: '2px',
    border: 'none',
    boxShadow: 'var(--q-page-shadow)',
    transition: 'none',
    '& .ProseMirror': {
      ...(baseSx['& .ProseMirror'] || {}),
      minHeight: paper.height,
      padding: 'var(--q-page-margin-y) var(--q-page-margin-x)',
    },
  };
};

/**
 * Canvas behind the sheets in Pages view (an inert wrapper in the other views, kept mounted so
 * switching views never remounts the editor).
 * @param {boolean} pagesView - Pages view on.
 * @returns {object} sx.
 */
const canvasSx = (pagesView) => (pagesView
  ? {
    bgcolor: 'var(--q-canvas, #EEECE7)',
    borderRadius: '8px',
    px: { xs: 1, sm: `${PAGES_CANVAS_PADDING}px` },
    py: `${PAGES_CANVAS_PADDING}px`,
    overflowX: 'auto',
  }
  : {});

/**
 * Read a localStorage value (private windows may throw).
 * @param {string} key - Storage key.
 * @returns {string|null} Value.
 */
const readPref = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

/**
 * Write a localStorage value, ignoring failures.
 * @param {string} key - Storage key.
 * @param {string} value - Value.
 * @returns {void}
 */
const writePref = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
};

/**
 * Strips legacy header/footer HTML-comment metadata from content (documents saved before the
 * header/footer simplification).
 * @param {string} htmlContent - HTML that may contain legacy metadata comments.
 * @returns {string} Clean HTML.
 */
export const stripLegacyMetadata = (htmlContent) => {
  if (!htmlContent) return '';
  return String(htmlContent)
    .replace(/<!--HEADER_FOOTER_START-->[\s\S]*?<!--HEADER_FOOTER_END-->\s*/g, '')
    .replace(/^\s*\{\s*"showHeaderFooter"[\s\S]*?\}\s*$/gm, '')
    .replace(/\n{4,}/g, '\n\n\n');
};

/**
 * Remove `data-block-id` attributes (used for legacy HTML-diff paths and AI payloads).
 * @param {string} html - HTML.
 * @returns {string} HTML without block ids.
 */
const stripBlockIds = (html) => String(html || '').replace(/\s+data-block-id="[^"]*"/g, '');

/**
 * Normalize incoming HTML for the editor.
 * @param {string} html - Raw HTML.
 * @param {'document'|'template'} mode - Editor mode.
 * @returns {string} Prepared HTML.
 */
const prepareContent = (html, mode) => {
  const cleaned = convertMermaidBlocks(stripLegacyMetadata(html || ''));
  return mode === 'template' ? migrateLegacyPlaceholders(cleaned) : cleaned;
};

/**
 * Quill's TipTap 3 editor. Uncontrolled: content is passed once (`initialContent`, re-applied
 * when `documentKey` changes) and reported back through a debounced `onChange`. Exposes the
 * editor bridge (CONTRACTS §6) through its ref.
 *
 * @param {object} props - Props.
 * @param {string} [props.initialContent] - HTML to load.
 * @param {string|number} [props.documentKey] - When it changes, `initialContent` is loaded (history reset). Without it, external `initialContent` changes are synced (legacy behaviour).
 * @param {(html: string) => void} [props.onChange] - Debounced content callback.
 * @param {({editor, html}) => void} [props.onUpdate] - Debounced legacy callback.
 * @param {() => void} [props.onDirty] - Called synchronously on every user change (cheap).
 * @param {() => void} [props.onSave] - Save handler (Mod-S, "Save now" in the toolbar's More menu).
 * @param {{status: string, lastSavedAt?: number|null, error?: string|null}} [props.saveState] - Status bar save state. Omit when the host shows it (template and document headers do).
 * @param {Array<{key: string, label: string, icon?: React.ReactNode, onClick: () => void}>} [props.menuExtras] - Host items for the toolbar's More menu.
 * @param {'document'|'template'} [props.mode] - Template mode enables AI fields, variables and locks.
 * @param {{documentId?: string, templateId?: string, title?: string}} [props.meta] - Bridge metadata.
 * @param {'page'|'wide'|'pages'} [props.pageMode] - Page width (A4 page, full width) or Pages view (print layout with page breaks).
 * @param {(mode: 'page'|'wide'|'pages') => void} [props.onPageModeChange] - Page mode change callback.
 * @param {'letter'|'a4'} [props.paperSize] - Pages view paper size (default: the stored preference, Letter).
 * @param {(size: 'letter'|'a4') => void} [props.onPaperSizeChange] - Paper size change callback.
 * @param {object|null} [props.pageSetup] - Page setup for Pages view (orientation, margins, header, footer; null = defaults). Default: the PageSetupProvider around the editor, if any.
 * @param {{title?: string, customer?: string, project?: string, date?: string}|null} [props.fieldContext] - Live-field values the header and footer show. Default: the PageSetupProvider's.
 * @param {boolean} [props.focusMode] - Hide chrome; show a floating mini toolbar.
 * @param {(on: boolean) => void} [props.onRequestFocusMode] - Ask the host to enter/leave focus mode.
 * @param {{onRegenerate: (info: object) => void, onAsk?: (info: object) => void}} [props.generatedSectionActions] - Hover/selection actions on generated sections ("Regenerate with Inky", "Ask Inky about this"); see GeneratedSectionChrome.
 * @param {(text: string) => void} [props.onAskInChat] - "Ask Inky in chat" from the inline AI menu.
 * @param {(ready: boolean) => void} [props.onReady] - Called with true once the editor (and its ref API) exists, false on teardown.
 * @param {boolean} [props.readOnly] - Read-only (applied live via setEditable).
 * @param {boolean} [props.showToolbar] - Show the toolbar.
 * @param {boolean} [props.showOutline] - Allow the outline panel (default: full-size editors).
 * @param {boolean} [props.showStatusBar] - Show the status bar (default: full-size editors).
 * @param {string} [props.placeholder] - Empty-document placeholder.
 * @param {number} [props.minHeight] - Minimum content height.
 * @param {boolean} [props.enableAdvancedFormatting] - Font family/size/colors.
 * @param {boolean} [props.enableImages] - Image upload/paste/drop.
 * @param {boolean} [props.isFullscreen] - Fill the parent height.
 * @param {string} [props.className] - Root class.
 * @param {object} [props.style] - Root style.
 */
const EnhancedRichTextEditor = forwardRef(({
  initialContent = '',
  documentKey,
  onChange,
  onUpdate,
  onDirty,
  onSave = null,
  saveState = null,
  menuExtras = null,
  mode = 'document',
  meta = null,
  pageMode: pageModeProp,
  onPageModeChange,
  paperSize: paperSizeProp,
  onPaperSizeChange,
  pageSetup: pageSetupProp,
  fieldContext: fieldContextProp,
  focusMode = false,
  onRequestFocusMode,
  generatedSectionActions = null,
  onAskInChat,
  onReady,
  readOnly = false,
  showToolbar = true,
  showOutline,
  showStatusBar,
  placeholder = '',
  minHeight = 400,
  enableAdvancedFormatting = true,
  enableImages = true,
  isFullscreen = false,
  className = '',
  style = {},
}, ref) => {
  const theme = useTheme();
  const { state: authState } = useAuth();
  const accountId = authState?.activeAccount?.id;
  const compact = minHeight < 400;
  const outlineAllowed = showOutline ?? (showToolbar && !compact);
  const statusAllowed = showStatusBar ?? (showToolbar && !compact);

  // ---------- refs that keep the editor uncontrolled ----------
  const initialContentRef = useRef(null);
  if (initialContentRef.current === null) initialContentRef.current = prepareContent(initialContent, mode);
  const lastLoadedRef = useRef(initialContent);
  const lastEmittedRef = useRef(null);
  const loadedKeyRef = useRef(documentKey);
  const pendingRef = useRef(false);
  const timerRef = useRef(null);
  const editorRef = useRef(null);
  const readOnlyRef = useRef(readOnly);
  const modeRef = useRef(mode);
  const metaRef = useRef(meta);
  const lastDiffMetricsRef = useRef(null);
  const listenersRef = useRef(new Map());
  const imageInputRef = useRef(null);
  const pageRef = useRef(null);
  const getPageElement = useCallback(() => pageRef.current, []);
  const rootRef = useRef(null);
  const propsRef = useRef({});
  propsRef.current = { onChange, onUpdate, onDirty, onSave, onRequestFocusMode, onReady };
  readOnlyRef.current = readOnly;
  modeRef.current = mode;
  metaRef.current = meta;

  // ---------- UI state ----------
  const [findOpen, setFindOpen] = useState(false);
  const [findToken, setFindToken] = useState(0);
  const [aiRequest, setAiRequest] = useState(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [imageAlt, setImageAlt] = useState(null);
  const [rootWidth, setRootWidth] = useState(1200);
  const [outlineOpen, setOutlineOpen] = useState(() => {
    const stored = readPref(OUTLINE_STORAGE_KEY);
    return stored === null ? true : stored === '1';
  });
  const [pageModeState, setPageModeState] = useState(() => normalizePageMode(pageModeProp || readPref(PAGE_MODE_STORAGE_KEY)));
  useEffect(() => {
    if (pageModeProp) setPageModeState(normalizePageMode(pageModeProp));
  }, [pageModeProp]);
  const pageMode = pageModeProp ? normalizePageMode(pageModeProp) : pageModeState;
  const [paperSizeState, setPaperSizeState] = useState(() => normalizePaperSize(paperSizeProp || readPaperSize()));
  // Follow the shared preference when another editor or the host's menu changes it.
  useEffect(() => subscribePaperSize(setPaperSizeState), []);
  const paperSize = paperSizeProp ? normalizePaperSize(paperSizeProp) : paperSizeState;
  /** Pages view (print layout); small embedded editors stay continuous. */
  const pagesView = pageMode === 'pages' && !compact;
  /** Layout actually shown ('pages' falls back to 'page' in compact editors). */
  const viewMode = pagesView ? 'pages' : (pageMode === 'pages' ? 'page' : pageMode);
  // Page setup (orientation, margins, header, footer) and the live-field values: the props, or
  // the PageSetupProvider of the document or template around the editor.
  const pageSetupCtx = usePageSetup();
  const pageSetupRaw = pageSetupProp !== undefined ? pageSetupProp : (pageSetupCtx ? pageSetupCtx.pageSetup : null);
  const fieldContextRaw = fieldContextProp !== undefined ? fieldContextProp : (pageSetupCtx ? pageSetupCtx.fieldContext : null);
  const pageSetupKey = pageSetupRaw == null ? '' : JSON.stringify(normalizePageSetup(pageSetupRaw));
  const fieldContextKey = fieldContextRaw && typeof fieldContextRaw === 'object' ? JSON.stringify(fieldContextRaw) : '';
  // Stable objects, so a host re-rendering with an equal value never re-paginates.
  const pageSetup = useMemo(() => (pageSetupKey ? JSON.parse(pageSetupKey) : null), [pageSetupKey]);
  const fieldContext = useMemo(() => (fieldContextKey ? JSON.parse(fieldContextKey) : null), [fieldContextKey]);
  const sheetSpec = useMemo(() => pagesSheetSpec(paperSize, pageSetup), [paperSize, pageSetup]);

  /**
   * Emit a bridge event to `on(event, cb)` subscribers.
   * @param {string} event - Event name.
   * @param {object} payload - Payload.
   * @returns {void}
   */
  const emit = useCallback((event, payload) => {
    const set = listenersRef.current.get(event);
    if (!set) return;
    set.forEach((cb) => {
      try {
        cb(payload);
      } catch (error) {
        console.error(`Editor event handler for "${event}" failed:`, error);
      }
    });
  }, []);

  /** Push pending content to the parent now. */
  const flush = useCallback(() => {
    clearTimeout(timerRef.current);
    if (!pendingRef.current) return;
    pendingRef.current = false;
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return;
    const html = ed.getHTML();
    lastEmittedRef.current = html;
    propsRef.current.onChange?.(html);
    propsRef.current.onUpdate?.({ editor: ed, html });
    emit('content-changed', { html });
  }, [emit]);

  // ---------- image handling (read through a ref so editorProps stay stable) ----------
  const handlersRef = useRef({});
  handlersRef.current.uploadImages = async (files) => {
    const ed = editorRef.current;
    if (!ed || !enableImages || !files?.length) return;
    for (const file of Array.from(files)) {
      if (!file.type?.startsWith('image/')) continue;
      try {
        const imageData = await imageService.uploadImage(file);
        if (!imageData || ed.isDestroyed) continue;
        const width = imageData.width ? Math.min(imageData.width, 600) : undefined;
        ed.chain().focus().setImage({
          src: imageData.dataUrl,
          alt: imageData.alt || imageData.name,
          title: imageData.name,
          ...(width ? { width } : {}),
          align: 'center',
          'data-image-id': imageData.id,
        }).run();
      } catch (error) {
        console.error('Image upload failed:', error);
      }
    }
  };
  handlersRef.current.onUpdate = () => {
    if (reviewControllerRef.current?.isActive()) return;
    pendingRef.current = true;
    propsRef.current.onDirty?.();
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, CHANGE_DEBOUNCE_MS);
  };
  handlersRef.current.flush = flush;

  const extensions = useMemo(() => buildEditorExtensions({ enableImages }), [enableImages]);
  const editorProps = useMemo(() => ({
    attributes: { class: 'q-prose', spellcheck: 'true' },
    handleDrop: (view, event, slice, moved) => {
      const files = Array.from(event.dataTransfer?.files || []).filter((f) => f.type.startsWith('image/'));
      if (moved || !files.length || !enableImages) return false;
      event.preventDefault();
      handlersRef.current.uploadImages(files);
      return true;
    },
    handlePaste: (view, event) => {
      const files = Array.from(event.clipboardData?.files || []).filter((f) => f.type.startsWith('image/'));
      if (!files.length || !enableImages) return false;
      event.preventDefault();
      handlersRef.current.uploadImages(files);
      return true;
    },
  }), [enableImages]);

  const editor = useEditor({
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    extensions,
    editorProps,
    content: initialContentRef.current,
    onUpdate: () => handlersRef.current.onUpdate(),
    onBlur: () => handlersRef.current.flush(),
  }, [enableImages]);
  editorRef.current = editor;

  const { review, setActiveIndex, controller } = useReviewController(editor, {
    readOnlyRef,
    emit,
    beforeEnter: flush,
  });
  const reviewControllerRef = useRef(controller);
  reviewControllerRef.current = controller;

  // Keep runtime storage in sync with props (read lazily by extensions and node views).
  useLayoutEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const runtime = editor.storage.quillRuntime;
    if (!runtime) return;
    runtime.mode = mode;
    runtime.placeholder = placeholder;
    runtime.enableImages = enableImages;
    runtime.callbacks = {
      openImagePicker: enableImages ? () => imageInputRef.current?.click() : null,
      openInlineAi: (opts) => setAiRequest({ action: opts?.action || null, at: Date.now() }),
      openFind: () => {
        setFindOpen(true);
        setFindToken((t) => t + 1);
      },
      openShortcuts: () => setShortcutsOpen(true),
    };
  });

  // Pages view settings last sent to the Pagination extension. A fresh EditorState (content
  // load, reopened review) re-initializes plugin state, so they are sent again after one.
  const paginationRef = useRef({ enabled: false, paperSize: 'letter', pageSetup: null, fieldContext: null });
  const resyncPagination = useCallback((ed) => {
    if (!ed || ed.isDestroyed || !paginationRef.current.enabled) return;
    ed.commands.setPagination?.({ ...paginationRef.current });
  }, []);

  /**
   * Load HTML silently: no undo entry, no onChange, history reset.
   * @param {string} html - HTML to load.
   * @returns {void}
   */
  const loadContent = useCallback((html) => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed) return;
    reviewControllerRef.current?.discard();
    clearTimeout(timerRef.current);
    pendingRef.current = false;
    const doc = createDocument(prepareContent(html, modeRef.current), ed.schema);
    const fresh = EditorState.create({ doc, plugins: ed.state.plugins });
    ed.view.updateState(fresh);
    setEditorEditable(ed, !readOnlyRef.current);
    const tr = buildBlockIdStampTransaction(ed.state) || ed.state.tr;
    tr.setMeta('addToHistory', false);
    tr.setMeta('preventUpdate', true);
    tr.setMeta('quillContentLoaded', true);
    ed.view.dispatch(tr);
    resyncPagination(ed);
    lastLoadedRef.current = html;
    lastEmittedRef.current = null;
  }, [resyncPagination]);

  // Documents saved with leaked review markup (old bug) re-open in review mode instead of
  // silently keeping red/green text.
  const reopenLeakedReview = useCallback(() => {
    const ed = editorRef.current;
    if (!ed || ed.isDestroyed || !docHasReviewMarkup(ed.state.doc)) return;
    const leaked = ed.state.doc;
    const baseline = getReviewBaselineDoc(ed.state);
    ed.view.updateState(EditorState.create({ doc: baseline, plugins: ed.state.plugins }));
    // Read-only views just show the clean document; editable ones let the user decide.
    if (!readOnlyRef.current) reviewControllerRef.current.enterWithDoc(leaked, { label: 'Unresolved AI changes', origin: 'leaked' });
    else ed.view.dispatch(ed.state.tr.setMeta('addToHistory', false).setMeta('preventUpdate', true).setMeta('quillContentLoaded', true));
    resyncPagination(ed);
  }, [resyncPagination]);

  useEffect(() => {
    if (!editor) return;
    setEditorEditable(editor, !readOnlyRef.current);
    reopenLeakedReview();
  }, [editor, reopenLeakedReview]);

  // Load new content when the document identity changes.
  useEffect(() => {
    if (!editor || documentKey === undefined || loadedKeyRef.current === documentKey) return;
    loadedKeyRef.current = documentKey;
    loadContent(initialContent || '');
    reopenLeakedReview();
    // initialContent is intentionally read only when documentKey changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, documentKey, loadContent, reopenLeakedReview]);

  // Legacy hosts without documentKey: sync genuinely external content changes only.
  useEffect(() => {
    if (!editor || documentKey !== undefined) return;
    const next = initialContent || '';
    if (next === (lastLoadedRef.current || '') || next === lastEmittedRef.current) return;
    loadContent(next);
    reopenLeakedReview();
  }, [editor, initialContent, documentKey, loadContent, reopenLeakedReview]);

  // readOnly applies live.
  useEffect(() => {
    if (editor && !controller.isActive()) setEditorEditable(editor, !readOnly);
  }, [editor, readOnly, controller]);

  // Tell the host when the editor (and therefore the ref API) is usable.
  useEffect(() => {
    if (!editor) return undefined;
    propsRef.current.onReady?.(true);
    return () => propsRef.current.onReady?.(false);
  }, [editor]);

  // Flush pending content on unmount.
  useEffect(() => () => {
    handlersRef.current.flush?.();
  }, []);

  // Track width for the outline and toolbar layout.
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const width = Math.round(entries[0].contentRect.width);
      setRootWidth((prev) => (Math.abs(prev - width) > 8 ? width : prev));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [editor]);

  // ---------- review navigation & shortcuts ----------
  const activeChange = review ? review.changes[review.activeIndex] : null;

  const navigateChange = useCallback((dir) => {
    if (!review || !review.changes.length) return;
    const next = (review.activeIndex + dir + review.changes.length) % review.changes.length;
    setActiveIndex(next);
    const change = review.changes[next];
    const el = editor?.view.dom.querySelector(`[data-change-id="${change.id}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [review, setActiveIndex, editor]);

  useEffect(() => {
    if (!review) return undefined;
    const onKey = (event) => {
      const target = event.target;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (target?.isContentEditable && !editor?.view.dom.contains(target))) return;
      const mod = event.metaKey || event.ctrlKey;
      if (event.altKey && !mod && (event.code === 'BracketRight' || event.key === ']')) navigateChange(1);
      else if (event.altKey && !mod && (event.code === 'BracketLeft' || event.key === '[')) navigateChange(-1);
      else if (mod && event.shiftKey && event.key === 'Enter') controller.acceptAll();
      else if (mod && event.key === 'Enter' && activeChange) controller.acceptChange(activeChange.id);
      else if (mod && event.key === 'Backspace' && activeChange) controller.rejectChange(activeChange.id);
      else return;
      event.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [review, activeChange, controller, navigateChange, editor]);

  // ---------- editor-wide shortcuts (Mod-S / Mod-F / Mod-J / Mod-/) ----------
  const openInlineAi = useCallback(() => {
    if (!editor || !editor.isEditable || controller.isActive()) return;
    setAiRequest({ action: null, at: Date.now() });
  }, [editor, controller]);

  const handleSave = useCallback(() => {
    flush();
    propsRef.current.onSave?.();
  }, [flush]);

  const onKeyDownCapture = useCallback((event) => {
    const mod = event.metaKey || event.ctrlKey;
    if (event.key === 'Escape' && focusMode && !aiRequest && !findOpen && editor?.view.dom.contains(event.target)) {
      propsRef.current.onRequestFocusMode?.(false);
      return;
    }
    if (!mod || event.altKey) return;
    const key = event.key.toLowerCase();
    if (key === 's') {
      event.preventDefault();
      event.stopPropagation();
      handleSave();
    } else if (key === 'f' && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      setFindOpen(true);
      setFindToken((t) => t + 1);
    } else if (key === 'j' && !event.shiftKey && editor && !editor.state.selection.empty && editor.isEditable) {
      // With a selection Mod-J asks AI about it; otherwise it bubbles (the app uses it to toggle Inky).
      event.preventDefault();
      event.stopPropagation();
      openInlineAi();
    } else if (key === '/' || event.code === 'Slash') {
      event.preventDefault();
      event.stopPropagation();
      setShortcutsOpen(true);
    }
  }, [focusMode, aiRequest, findOpen, editor, handleSave, openInlineAi]);

  // ---------- bridge (CONTRACTS §6) ----------
  const api = useMemo(() => {
    const ed = editor;
    const alive = () => Boolean(ed && !ed.isDestroyed);

    /** Make sure every top-level block has an id before ids are handed out. */
    const ensureBlockIds = () => {
      if (!alive() || controller.isActive()) return;
      const tr = buildBlockIdStampTransaction(ed.state);
      if (!tr) return;
      tr.setMeta('addToHistory', false);
      tr.setMeta('preventUpdate', true);
      ed.view.dispatch(tr);
    };

    /**
     * Refusal reason when a review from a different origin is open. A new suggestion set
     * replaces the open one only when both come from the same source (e.g. Inky refining its
     * own edit); anything else (a regenerated section, reopened unresolved changes) must be
     * accepted or rejected first so it never disappears without the user deciding.
     * @param {string} origin - Origin of the incoming suggestions.
     * @returns {string|null} Reason or null when the incoming set may proceed.
     */
    const pendingReviewConflict = (origin) => {
      const pending = controller.getPending();
      if (!pending || pending.origin === origin) return null;
      const count = pending.changes.length;
      return `review_pending: resolve the open "${pending.label}" suggestions (${count} change${count === 1 ? '' : 's'}) before new ones can be shown`;
    };

    /**
     * Enter a review from two HTML strings (full-document token diff).
     * @param {string} baseHtml - Current HTML.
     * @param {string} nextHtml - Proposed HTML.
     * @param {string} label - Review label.
     * @param {string} [origin] - Review origin (defaults to the label).
     * @returns {{hasChanges: boolean, metrics: object, reviewId: string|null}} Result.
     */
    const reviewFromHtml = (baseHtml, nextHtml, label, origin) => {
      // Ids are stripped for the diff and adopted back from the live document on entry.
      ensureBlockIds();
      const { mergedHTML, hasChanges, metrics } = buildMergedReviewHtml(
        stripBlockIds(baseHtml),
        stripBlockIds(prepareContent(nextHtml, modeRef.current))
      );
      lastDiffMetricsRef.current = metrics;
      if (!hasChanges) return { hasChanges: false, metrics, reviewId: null };
      const result = controller.enterWithHtml(mergedHTML, { label, origin: origin || label });
      return { hasChanges: Boolean(result.reviewId), metrics, reviewId: result.reviewId };
    };

    const bridge = {
      /** Bridge metadata. */
      getMeta: () => ({
        documentId: metaRef.current?.documentId ?? null,
        templateId: metaRef.current?.templateId ?? null,
        kind: modeRef.current === 'template' ? 'template' : 'document',
        title: metaRef.current?.title ?? null,
        readOnly: Boolean(readOnlyRef.current),
      }),
      /** Clean HTML (during review: decisions so far, pending changes excluded). */
      getHTML: () => (alive() ? controller.getBaselineHtml() : ''),
      /** @deprecated Alias of getHTML(). */
      getHTMLWithoutMetadata: () => (alive() ? controller.getBaselineHtml() : ''),
      /** Plain text of the clean document. */
      getText: () => {
        if (!alive()) return '';
        const doc = controller.getBaselineDoc();
        return doc.textBetween(0, doc.content.size, '\n\n', ' ');
      },
      /** Selection from the ProseMirror model. */
      getSelection: () => {
        if (!alive()) return null;
        ensureBlockIds();
        const { from, to, empty } = ed.state.selection;
        const { doc } = ed.state;
        const blockIds = [];
        doc.forEach((node, pos) => {
          if (pos + node.nodeSize > from && pos <= Math.max(from, to - 1) && node.attrs.blockId) blockIds.push(node.attrs.blockId);
        });
        return {
          from,
          to,
          text: empty ? '' : doc.textBetween(from, to, '\n', ' '),
          html: empty ? '' : getHTMLFromFragment(doc.slice(from, to).content, ed.schema),
          blockIds,
        };
      },
      /** Top-level blocks with stable ids. */
      getBlocks: () => {
        if (!alive()) return [];
        ensureBlockIds();
        const doc = controller.getBaselineDoc();
        return getTopLevelBlocks(doc).map(({ id, node, pos }) => ({
          id,
          type: node.type.name,
          ...(node.type.name === 'heading' ? { level: node.attrs.level } : {}),
          // Same text replace_text matches against: one line per paragraph/item/cell, inline
          // atoms (field chips, line breaks) as U+FFFC.
          text: doc.textBetween(pos, pos + node.nodeSize, '\n', INLINE_ATOM_CHAR),
          html: getHTMLFromFragment(Fragment.from(node), ed.schema),
          locked: Boolean(node.attrs.tplLocked),
        }));
      },
      /**
       * Apply a block-anchored edit plan.
       * @param {Array<object>} ops - Plan ops.
       * `origin` identifies the source of the suggestions (defaults to the label); an open review
       * from another origin is never replaced (see pendingReviewConflict).
       * @param {{review?: boolean, label?: string, origin?: string, allowLargeDeletion?: boolean, allowFieldRemoval?: boolean}} [options] - Options.
       * @returns {{applied: Array<object>, failed: Array<{op: object, reason: string}>, reviewId: string|null}} Result.
       */
      applyEditPlan: (ops, options = {}) => {
        const list = Array.isArray(ops) ? ops : [];
        if (!alive()) return { applied: [], failed: list.map((op) => ({ op, reason: 'editor_unavailable' })), reviewId: null };
        if (readOnlyRef.current && options.review === false) {
          return { applied: [], failed: list.map((op) => ({ op, reason: 'read_only' })), reviewId: null };
        }
        const label = options.label || 'AI edit';
        const origin = String(options.origin || label);
        const conflict = pendingReviewConflict(origin);
        if (conflict) return { applied: [], failed: list.map((op) => ({ op, reason: conflict })), reviewId: null };
        flush();
        ensureBlockIds();
        const inReview = controller.isActive();
        const baseState = controller.getBaselineState();
        const direct = applyPlanToTransaction(baseState, list, { mode: 'direct', allowFieldRemoval: options.allowFieldRemoval });
        if (direct.applied.length === 0) return { applied: [], failed: direct.failed, reviewId: null };
        const gutted = options.allowLargeDeletion ? null : guttedEditReason(baseState.doc, direct.tr.doc);
        if (gutted) return { applied: [], failed: list.map((op) => ({ op, reason: gutted })), reviewId: null };

        if (options.review === false) {
          if (inReview) controller.commitDoc(direct.tr.doc);
          else {
            direct.tr.setMeta('quillEditPlan', label);
            ed.view.dispatch(direct.tr);
          }
          emit('plan-applied', { applied: direct.applied, failed: direct.failed, review: false });
          return { applied: direct.applied.map(({ op }) => ({ op })), failed: direct.failed, reviewId: null };
        }

        const plan = applyPlanToTransaction(baseState, list, {
          mode: 'review',
          allowFieldRemoval: options.allowFieldRemoval,
          changeIdPrefix: `p${Date.now().toString(36)}_`,
        });
        const result = controller.enterWithDoc(plan.tr.doc, {
          label,
          origin,
          tr: inReview ? undefined : plan.tr,
        });
        return { applied: plan.applied, failed: plan.failed, reviewId: result.reviewId };
      },
      /**
       * Full-rewrite fallback with the same review UI.
       * @param {string} html - Proposed HTML.
       * @param {{label?: string, origin?: string, allowLargeDeletion?: boolean}} [options] - Options.
       * @returns {{reviewId: string|null, hasChanges: boolean, refused?: string}} Result.
       */
      setContentWithReview: (html, options = {}) => {
        if (!alive()) return { reviewId: null, hasChanges: false, refused: 'editor_unavailable' };
        const label = options.label || 'AI rewrite';
        const origin = String(options.origin || label);
        const conflict = pendingReviewConflict(origin);
        if (conflict) return { reviewId: null, hasChanges: false, refused: conflict };
        flush();
        const baseDoc = controller.getBaselineDoc();
        if (!options.allowLargeDeletion) {
          const nextDoc = createDocument(prepareContent(html, modeRef.current), ed.schema);
          const reason = guttedEditReason(baseDoc, nextDoc);
          if (reason) return { reviewId: null, hasChanges: false, refused: reason };
        }
        const result = reviewFromHtml(docToHtml(baseDoc, ed.schema), html, label, origin);
        return { reviewId: result.reviewId, hasChanges: result.hasChanges };
      },
      getPendingReview: () => controller.getPending(),
      acceptAll: () => controller.acceptAll(),
      rejectAll: () => controller.rejectAll(),
      acceptChange: (id) => controller.acceptChange(id),
      rejectChange: (id) => controller.rejectChange(id),
      /** Scroll a top-level block into view and flash it. */
      scrollToBlock: (id) => {
        if (!alive() || !id) return false;
        let target = null;
        ed.state.doc.forEach((node, pos) => {
          if (!target && node.attrs.blockId === id) target = pos;
        });
        if (target === null) return false;
        const dom = ed.view.nodeDOM(target);
        if (!dom || !dom.scrollIntoView) return false;
        dom.scrollIntoView({ behavior: 'smooth', block: 'center' });
        dom.classList?.add('q-block-flash');
        setTimeout(() => dom.classList?.remove('q-block-flash'), 1300);
        return true;
      },
      /**
       * Focus the editor. Pass `{ scrollIntoView: false }` to keep the viewport where it is
       * (TipTap otherwise scrolls the current selection into view one frame later, which would
       * undo a scrollToBlock started just before).
       * @param {null|'start'|'end'|'all'|number|boolean} [position] - TipTap focus position.
       * @param {{scrollIntoView?: boolean}} [options] - Focus options.
       */
      focus: (position = null, options = {}) => {
        if (alive()) ed.commands.focus(position, { scrollIntoView: options?.scrollIntoView !== false });
      },
      /**
       * Subscribe to 'review-started' | 'review-resolved' | 'content-changed' | 'plan-applied'.
       * @param {string} event - Event name.
       * @param {Function} cb - Listener.
       * @returns {() => void} Unsubscribe.
       */
      on: (event, cb) => {
        if (typeof cb !== 'function') return () => {};
        if (!listenersRef.current.has(event)) listenersRef.current.set(event, new Set());
        listenersRef.current.get(event).add(cb);
        return () => listenersRef.current.get(event)?.delete(cb);
      },
      /** Push pending content to onChange immediately. */
      flush,
      /** Load HTML silently (no undo entry, history reset). */
      loadContent,

      // ----- legacy methods used by the doc.edit tool (App.js) -----
      /**
       * Set content. `{ emitUpdate: false }` loads silently (no history); otherwise the
       * replacement is one undoable step that reaches onChange.
       */
      setContent: (content, options) => {
        if (!alive()) return;
        if (options && options.emitUpdate === false) {
          loadContent(content || '');
          return;
        }
        if (controller.isActive()) controller.rejectAll();
        ed.chain().setContent(prepareContent(content || '', modeRef.current), { emitUpdate: true }).run();
      },
      /** HTML for AI edits (no review spans, no block ids). */
      getCleanHTMLForAiEdit: () => (alive() ? stripBlockIds(controller.getBaselineHtml()) : ''),
      showDiffEdits: (originalHTML, editedHTML) => {
        if (!alive()) return { hasChanges: false };
        const orig = typeof originalHTML === 'string' ? originalHTML.trim() : '';
        const edited = typeof editedHTML === 'string' ? editedHTML.trim() : '';
        if (!orig || !edited) return { hasChanges: false };
        const result = reviewFromHtml(orig, edited, 'AI edit');
        return { hasChanges: result.hasChanges, metrics: result.metrics };
      },
      showPatchDiffEdits: (originalHTML, operations) => {
        if (!alive()) return { hasChanges: false };
        const orig = typeof originalHTML === 'string' ? originalHTML.trim() : '';
        if (!orig) return { hasChanges: false };
        ensureBlockIds();
        const { mergedHTML, hasChanges, metrics, failedOperations } = buildPatchDiffHtml(stripBlockIds(orig), operations);
        lastDiffMetricsRef.current = metrics || null;
        if (failedOperations.length > 0 || !hasChanges) return { hasChanges: false, metrics, failedOperations };
        const result = controller.enterWithHtml(mergedHTML, { label: 'AI edit' });
        return { hasChanges: Boolean(result.reviewId), metrics, failedOperations: [] };
      },
      acceptAllChanges: () => controller.acceptAll(),
      rejectAllChanges: () => controller.rejectAll(),
      isDestroyed: () => !alive(),
      getLastDiffMetrics: () => lastDiffMetricsRef.current,
      scrollToTrustSection: (sectionIndex) => {
        if (!alive() || typeof sectionIndex !== 'number' || sectionIndex < 0) return;
        const el = ed.view.dom.querySelectorAll('[data-trust-section="true"]').item(sectionIndex);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
      /**
       * Select a generated section's whole content (block field, inline field value or trust
       * section) so Inky's edit targets exactly it. Found by field id, else by its DOM element.
       * @param {{fieldId?: string|null, element?: HTMLElement|null}} target - Section.
       * @returns {{ok: boolean, inline: boolean, text: string}} Result.
       */
      selectGeneratedSection: ({ fieldId = null, element = null } = {}) => {
        const none = { ok: false, inline: false, text: '' };
        if (!alive()) return none;
        const { view } = ed;
        let el = null;
        if (fieldId) el = Array.from(view.dom.querySelectorAll('[data-field-id]')).find((node) => node.getAttribute('data-field-id') === fieldId) || null;
        if (!el && element && view.dom.contains(element)) el = element;
        if (!el) return none;
        try {
          const from = view.posAtDOM(el, 0);
          const to = view.posAtDOM(el, el.childNodes.length);
          const { state } = view;
          const size = state.doc.content.size;
          const $from = state.doc.resolve(Math.min(Math.max(from, 0), size));
          const $to = state.doc.resolve(Math.min(Math.max(to, from), size));
          const tr = state.tr.setSelection(TextSelection.between($from, $to));
          tr.setMeta('addToHistory', false);
          view.dispatch(tr);
          if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          return { ok: true, inline: el.tagName === 'SPAN', text: (el.textContent || '').replace(/\s+/g, ' ').trim() };
        } catch {
          return none;
        }
      },
      selectTrustSection: (sectionIndex) => {
        if (!alive() || typeof sectionIndex !== 'number' || sectionIndex < 0) return false;
        const el = ed.view.dom.querySelectorAll('[data-trust-section="true"]').item(sectionIndex);
        if (!el) return false;
        try {
          const pos = ed.view.posAtDOM(el, 0);
          const { state } = ed.view;
          const tr = state.tr.setSelection(TextSelection.near(state.doc.resolve(Math.min(Math.max(pos, 0), state.doc.content.size))));
          tr.setMeta('addToHistory', false);
          ed.view.dispatch(tr);
          if (ed.isEditable) ed.view.focus();
        } catch {
          /* position not resolvable: still scroll */
        }
        if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return true;
      },
      cleanContent: (content) => stripLegacyMetadata(content || ''),
    };
    Object.defineProperty(bridge, 'editor', { enumerable: true, get: () => editorRef.current });
    Object.defineProperty(bridge, 'isDiffMode', { enumerable: true, get: () => controller.isActive() });
    return bridge;
  }, [editor, controller, flush, loadContent, emit]);

  useImperativeHandle(ref, () => api, [api]);

  // ---------- reading position across view changes ----------
  // The layout key changes on Pages / Page width / Full width / paper size. The anchor is read
  // while rendering, before React commits the new layout, and pinned by the layout effect after
  // the pagination one below until the new layout has settled (see pinScrollAnchor).
  const layoutKey = `${viewMode}:${pagesView ? `${paperSize}:${sheetSpec.orientation}:${sheetSpec.margins}` : ''}`;
  const layoutKeyRef = useRef(layoutKey);
  const scrollAnchorRef = useRef(null);
  if (layoutKeyRef.current !== layoutKey) {
    layoutKeyRef.current = layoutKey;
    scrollAnchorRef.current = captureScrollAnchor(editor);
  }

  // ---------- pagination (Pages view) ----------
  // The Pagination extension (src/extensions/Pagination.js) only adds decorations; it is told
  // whenever the view, the paper or the page setup (orientation, margins, header and footer,
  // their live fields) changes. Guarded so the editor works without it.
  useLayoutEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (!pagesView && !paginationRef.current.enabled) return;
    paginationRef.current = { enabled: pagesView, paperSize, pageSetup, fieldContext };
    editor.commands.setPagination?.({ ...paginationRef.current });
  }, [editor, pagesView, paperSize, pageSetup, fieldContext]);

  // Declared after the pagination effect so the first restore already sees page ends switched
  // on or off; the pin then follows Pagination's measurement and anything else still moving.
  useLayoutEffect(() => {
    const anchor = scrollAnchorRef.current;
    scrollAnchorRef.current = null;
    if (!anchor || !editor) return undefined;
    return pinScrollAnchor(editor, anchor);
  }, [layoutKey, editor]);

  // ---------- layout ----------
  const sheetFits = pagesSheetFits(rootWidth, sheetSpec);
  const pageSx = useMemo(() => {
    const base = buildPageSx(theme, { pageMode: viewMode === 'wide' ? 'wide' : 'page', minHeight, compact });
    return pagesView ? buildPagesSheetSx(base, sheetSpec, { fits: sheetFits }) : base;
  }, [theme, viewMode, pagesView, sheetSpec, minHeight, compact, sheetFits]);
  const outlineRoom = outlineFits(rootWidth, viewMode, sheetSpec);
  const outlineVisible = outlineAllowed && outlineOpen && outlineRoom && !focusMode;
  const toolbarVisible = showToolbar && !readOnly && !review && !focusMode;

  const toggleOutline = useCallback(() => {
    setOutlineOpen((open) => {
      writePref(OUTLINE_STORAGE_KEY, open ? '0' : '1');
      return !open;
    });
  }, []);
  const changePageMode = useCallback((mode) => {
    const next = normalizePageMode(mode);
    writePref(PAGE_MODE_STORAGE_KEY, next);
    setPageModeState(next);
    onPageModeChange?.(next);
  }, [onPageModeChange]);
  const changePaperSize = useCallback((size) => {
    const next = writePaperSize(size);
    setPaperSizeState(next);
    onPaperSizeChange?.(next);
  }, [onPaperSizeChange]);
  const openFind = useCallback(() => {
    setFindOpen(true);
    setFindToken((t) => t + 1);
  }, []);
  const openImagePicker = useCallback(() => imageInputRef.current?.click(), []);
  const showShortcuts = useCallback(() => setShortcutsOpen(true), []);
  const editImageAlt = useCallback(() => {
    if (!editor) return;
    setImageAlt(editor.getAttributes('image')?.alt || '');
  }, [editor]);
  const closeAi = useCallback(() => setAiRequest(null), []);

  const rootSx = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: 0,
    ...(isFullscreen ? { height: '100%', minHeight: 0, flex: 1 } : {}),
    ...diffSpanSx(theme),
  };

  if (!editor) {
    return (
      <Box className={className} style={style} sx={rootSx}>
        {showToolbar && !readOnly && (
          <Skeleton variant="rounded" height={40} sx={{ mb: 1, borderRadius: 999 }} />
        )}
        {/* Same canvas and sheet geometry as the loaded editor, so nothing jumps when it mounts. */}
        <Box sx={canvasSx(pagesView)}>
          <Paper
            elevation={0}
            sx={{ ...pageSx, p: pagesView ? 'var(--q-page-margin-y) var(--q-page-margin-x)' : compact ? 2 : { xs: 3, md: 9 } }}
            aria-busy="true"
          >
            <Skeleton width="55%" height={36} />
            <Skeleton width="90%" />
            <Skeleton width="80%" />
            <Skeleton width="85%" />
          </Paper>
        </Box>
      </Box>
    );
  }

  return (
    <EditorContext.Provider value={{ editor }}>
      <Box
        ref={rootRef}
        className={className}
        style={style}
        sx={rootSx}
        onKeyDownCapture={onKeyDownCapture}
        data-editor-mode={mode}
      >
        {review && (
          <ReviewBar
            changes={review.changes}
            activeIndex={review.activeIndex}
            label={review.label}
            onPrev={() => navigateChange(-1)}
            onNext={() => navigateChange(1)}
            onAccept={controller.acceptChange}
            onReject={controller.rejectChange}
            onAcceptAll={controller.acceptAll}
            onRejectAll={controller.rejectAll}
          />
        )}

        {toolbarVisible && (
          <EditorToolbar
            editor={editor}
            mode={mode}
            enableAdvancedFormatting={enableAdvancedFormatting}
            enableImages={enableImages}
            onPickImage={enableImages ? openImagePicker : undefined}
            onEditImage={editImageAlt}
            onOpenFind={openFind}
            onOpenInlineAi={openInlineAi}
            onToggleOutline={outlineAllowed ? toggleOutline : undefined}
            outlineUnavailable={outlineAllowed && !outlineRoom
              ? (viewMode === 'wide' ? 'Needs a wider window' : 'Needs more room beside the page: use Full width or a wider window')
              : null}
            outlineOpen={outlineVisible}
            pageMode={pageMode}
            onSetPageMode={compact ? undefined : changePageMode}
            paperSize={paperSize}
            onSetPaperSize={compact ? undefined : changePaperSize}
            onShowShortcuts={showShortcuts}
            onSave={onSave ? handleSave : undefined}
            menuExtras={menuExtras}
          />
        )}

        <FindReplaceBar
          editor={editor}
          open={findOpen}
          focusToken={findToken}
          readOnly={readOnly || Boolean(review)}
          onClose={() => setFindOpen(false)}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            flex: 1,
            minHeight: 0,
            ...(compact ? {} : { px: { xs: 0, sm: 1 }, py: { xs: 1, sm: 2 } }),
          }}
        >
          {outlineVisible && (
            <Box
              sx={{
                width: OUTLINE_WIDTH,
                flexShrink: 0,
                position: 'sticky',
                top: 'calc(var(--q-editor-sticky-top, 0px) + 56px)',
                maxHeight: 'calc(100vh - var(--q-editor-sticky-top, 0px) - 80px)',
                overflowY: 'auto',
                borderRight: '1px solid',
                borderColor: 'divider',
                pr: 1,
              }}
            >
              <DocumentOutline editor={editor} onClose={toggleOutline} />
            </Box>
          )}

          <Box ref={pageRef} sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
            <Box
              sx={canvasSx(pagesView)}
              data-testid="editor-page-view"
              data-page-view={viewMode}
              data-paper-size={pagesView ? paperSize : undefined}
              data-orientation={pagesView ? sheetSpec.orientation : undefined}
              data-margins={pagesView ? sheetSpec.margins : undefined}
            >
              <Paper elevation={0} sx={pageSx}>
                {!readOnly && (
                  <Box
                    sx={{ visibility: review ? 'hidden' : undefined, pointerEvents: review ? 'none' : undefined }}
                    aria-hidden={review ? true : undefined}
                  >
                    <DragHandle editor={editor}>
                      <IconButton size="small" sx={{ p: 0.25, cursor: 'grab', color: 'text.secondary' }} aria-label="Drag block">
                        <DragIndicator fontSize="small" />
                      </IconButton>
                    </DragHandle>
                  </Box>
                )}
                <EditorContent editor={editor} />
              </Paper>
            </Box>
            {generatedSectionActions && !review && !readOnly && (
              <GeneratedSectionChrome
                editor={editor}
                getContainer={getPageElement}
                onRegenerate={generatedSectionActions.onRegenerate}
                onAsk={generatedSectionActions.onAsk}
              />
            )}
            {review && (
              <ReviewChangeChip
                editor={editor}
                container={pageRef.current}
                change={activeChange}
                version={review.version}
                onAccept={controller.acceptChange}
                onReject={controller.rejectChange}
              />
            )}
          </Box>
        </Box>

        {/* Sticky inside the main scroller (not fixed to the viewport), so it stays centred on
            the writing column and never covers a docked Inky panel. */}
        {focusMode && !readOnly && !review && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 24,
              alignSelf: 'center',
              width: 'min(760px, 100%)',
              mt: 2,
              zIndex: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <EditorToolbar
                editor={editor}
                mode={mode}
                enableAdvancedFormatting={false}
                enableImages={enableImages}
                onPickImage={enableImages ? openImagePicker : undefined}
                onShowShortcuts={showShortcuts}
                floating
              />
            </Box>
            {onRequestFocusMode && (
              <Tooltip title="Exit focus mode (Esc)">
                <IconButton
                  aria-label="Exit focus mode"
                  onClick={() => onRequestFocusMode(false)}
                  sx={{ bgcolor: 'var(--q-surface)', border: '1px solid', borderColor: 'divider', boxShadow: 'var(--q-shadow-xl)' }}
                >
                  <CloseFullscreen fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {statusAllowed && !focusMode && (
          <EditorStatusBar
            editor={editor}
            mode={mode}
            saveState={saveState}
            reviewCount={review ? review.changes.length : 0}
            onShowShortcuts={showShortcuts}
          />
        )}

        <SelectionBubbleMenu editor={editor} onAskAi={openInlineAi} />
        <SlashMenu editor={editor} />
        <InlineAiMenu
          editor={editor}
          request={aiRequest}
          onClose={closeAi}
          documentId={meta?.documentId || meta?.templateId || ''}
          accountId={accountId}
          onAskInChat={onAskInChat}
        />
        <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

        <Dialog open={imageAlt !== null} onClose={() => setImageAlt(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Image alt text</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="Describe the image"
              helperText="Used by screen readers and exports. Resize by dragging the image corners."
              value={imageAlt || ''}
              onChange={(e) => setImageAlt(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setImageAlt(null)}>Cancel</Button>
            <Button
              variant="contained"
              disableElevation
              onClick={() => {
                editor.chain().focus().updateAttributes('image', { alt: imageAlt || '' }).run();
                setImageAlt(null);
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {enableImages && (
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            hidden
            multiple
            onChange={(e) => {
              handlersRef.current.uploadImages(e.target.files);
              e.target.value = '';
            }}
          />
        )}
      </Box>
    </EditorContext.Provider>
  );
});

EnhancedRichTextEditor.displayName = 'EnhancedRichTextEditor';

export default EnhancedRichTextEditor;
